import { isNativeApp, isIOS, isAndroid } from './platform';
import { supabase } from '../config/supabase';

/**
 * Check if push notifications are available.
 * Only true inside the webtoapp.design native wrapper when the bridge is loaded.
 */
export function isPushAvailable() {
  return isNativeApp && typeof window.getNotificationToken === 'function';
}

/**
 * Request the push notification token from the webtoapp.design bridge.
 * Returns the token string, or null if unavailable/denied.
 */
export async function requestPushToken() {
  if (!isPushAvailable()) return null;
  try {
    const token = await window.getNotificationToken();
    return token || null;
  } catch (err) {
    if (import.meta.env.DEV) console.error('[Push] Token request failed:', err);
    return null;
  }
}

/**
 * Register a push token in Supabase for the given user.
 * Uses upsert to avoid duplicates (user_id + token unique constraint).
 */
export async function registerPushToken(userId, token) {
  if (!userId || !token) return;
  const platform = isIOS ? 'ios' : isAndroid ? 'android' : 'unknown';
  const { error } = await supabase
    .from('push_tokens')
    .upsert(
      { user_id: userId, token, platform, updated_at: new Date().toISOString() },
      { onConflict: 'user_id,token' }
    );
  if (error) throw error;
  if (import.meta.env.DEV) console.log('[Push] Token registered for user:', userId);
}

/**
 * Remove a push token from Supabase (e.g., on sign out).
 */
export async function removePushToken(userId, token) {
  if (!userId || !token) return;
  const { error } = await supabase
    .from('push_tokens')
    .delete()
    .eq('user_id', userId)
    .eq('token', token);
  if (error && import.meta.env.DEV) console.error('[Push] Token removal failed:', error);
}
